import { useSidebar } from "components/ui/sidebar";
import { useLocation, useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { FolderClosed, Loader2, MoreHorizontal } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useChatStore } from "../../../state/store/chat/use-chat-store";
import { useProjects } from "../hooks/use-projects";
import { ConversationActions } from "./conversation-actions";
import { ProjectActions } from "./project-actions";
// import { Tooltip, TooltipContent, TooltipTrigger } from "../../../components/ui/tooltip";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { useTranslation } from 'react-i18next';
import { useQueryClient } from "@tanstack/react-query";
import { fetchProjectConversations } from "../services/projects-services";
import { useConfig } from "hooks/use-config";
import { Droppable } from "features/sidebar/Dropabble";
import { SidebarTreeItem } from "components/blocks/layout/sidebar-items";
// import { IconName } from "components/blocks/menu-icon/menu-icon";

export const ProjectLists = () => {
    const isSidebarLoading = false;
    const { setOpenMobile, isMobile } = useSidebar();
    const { pathname } = useLocation();
    const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
    const [recentlyAccessedProjects, setRecentlyAccessedProjects] = useState<string[]>([]);
    const navigate = useNavigate();
    const sidebarProjectsList = useChatStore((state) => state.sidebarProjectsList);
    const updateProjectItems = useChatStore((state) => state.updateProjectItems);
    const setSidebarProjectsList = useChatStore((state) => state.setSidebarProjectsList);
    const { projectId } = useParams();
    const { data: projects } = useProjects({
        pageSize: 50,
        page: 1,
        order: "desc",
        searchQuery: "",
    });
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { configurationSettings } = useConfig();

    const [loadingProjects, setLoadingProjects] = useState<Record<string, boolean>>({});
    const previousConversationRef = useRef<string | null>(null);



    useEffect(() => {
        const isChatRoute = pathname.startsWith('/chat/');
        if (isChatRoute && sidebarProjectsList) {
            const afterChat = pathname.split('/chat/')[1] ?? '';
            const conversationId = afterChat.split('/')[0];
            const isNewConversation = conversationId !== previousConversationRef.current;
            previousConversationRef.current = conversationId;

            const projectWithConversation = sidebarProjectsList.find(project =>
                project.items?.some(item => item.ConversationId === conversationId)
            );

            if (projectWithConversation && isNewConversation) {
                setOpenGroups(prev => ({
                    ...prev,
                    [projectWithConversation.id]: true
                }));
            }
        }
    }, [pathname, sidebarProjectsList]);

    const toggleGroup = (groupId: string) => {
        setOpenGroups((prev) => ({
            ...prev,
            [groupId]: !prev[groupId],
        }));
    };

    useEffect(() => {
        if (projects) {
            setSidebarProjectsList(projects);
        }
    }, [setSidebarProjectsList, projects])

    if (isSidebarLoading || !sidebarProjectsList?.length) return null;

    const handleProjectClick = (projectId: string) => {
        if (isMobile) {
            setOpenMobile(false);
        }
        navigate(`/project/${projectId}`);
    }

    const handleProjectClickFromDropdown = (projectId: string) => {
        // Add to recently accessed projects
        setRecentlyAccessedProjects(prev => {
            const updated = [projectId, ...prev.filter(id => id !== projectId)];
            return updated.slice(0, 5); // Keep only last 5 recently accessed
        });
        if (isMobile) {
            setOpenMobile(false);
        }
        navigate(`/project/${projectId}`);
    }

    // Reorder projects to show recently accessed ones first
    const getOrderedProjects = () => {
        if (!sidebarProjectsList) return [];

        const recentlyAccessed = recentlyAccessedProjects
            .map(id => sidebarProjectsList.find(project => project.id === id))
            .filter((project): project is NonNullable<typeof project> => Boolean(project));

        const otherProjects = sidebarProjectsList.filter(
            project => !recentlyAccessedProjects.includes(project.id)
        );

        return [...recentlyAccessed, ...otherProjects];
    };

    const MAX_VISIBLE_PROJECTS = 5;
    const orderedProjects = getOrderedProjects();
    const visibleProjects = orderedProjects.slice(0, MAX_VISIBLE_PROJECTS);
    const hiddenProjects = orderedProjects.slice(MAX_VISIBLE_PROJECTS);
    const strokeWidth = 2.2;

    const handleFolderClick = async (projectId: string) => {
        if (projectId in loadingProjects || orderedProjects?.find(project => project.id === projectId)?.items?.length) return;

        setLoadingProjects((prev) => ({
            ...prev,
            [projectId]: true,
        }));

        try {
            const conversations = await queryClient.fetchQuery({
                queryKey: ["project-conversations", projectId],
                queryFn: () => fetchProjectConversations(configurationSettings, projectId)
            });

            updateProjectItems(projectId, conversations);
        } catch (error) {
            console.error('Error fetching project conversations:', error);
        } finally {
            setLoadingProjects((prev) => ({
                ...prev,
                [projectId]: false,
            }));
        }
    }

    const renderProject = (group: any) => (
        <SidebarTreeItem
            key={group.id}
            label={group.name}
            // icon={(loadingProjects[group.id] ? undefined : (openGroups[group.id] ? 'FolderOpen' : 'FolderClosed')) as IconName}
            isActive={projectId === group.id}
            defaultOpen={openGroups[group.id] || false}
            onClick={() => {
                toggleGroup(group.id);
                handleFolderClick(group.id);
                handleProjectClick(group.id);
            }}
            rightAction={<ProjectActions projectId={group.id} projectName={group.name} />}
        >
            {loadingProjects[group.id] && (
                <div className="flex items-center gap-2 px-4 py-1 text-xs text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <span>{t('LOADING')}...</span>
                </div>
            )}
            {group.items && group.items.length > 0 && (
                <div className="flex flex-col mt-1 gap-1">
                    {group.items.slice(0, 5).map((item: any) => (
                        <SidebarTreeItem
                            key={item.ConversationId}
                            label={item.ConversationTitle || t('NEW_CHAT')}
                            isActive={pathname.includes(item.ConversationId)}
                            depth={1}
                            onClick={() => {
                                if (isMobile) setOpenMobile(false);
                                navigate(`/chat/${item.ConversationId}/${group.id}`);
                            }}
                            rightAction={<ConversationActions conversationId={item.ConversationId} conversationTitle={item.ConversationTitle} projectId={group.id} projectName={group.name} />}
                        />
                    ))}
                </div>
            )}
            {group.items && group.items.length > 5 && (
                <SidebarTreeItem
                    label={`${t('SEE_ALL')} (${group.items.length})`}
                    // icon={'Ellipsis' as IconName}
                    depth={1}
                    onClick={() => handleProjectClick(group.id)}
                />
            )}
        </SidebarTreeItem>
    );

    return (
        <div className="flex flex-col gap-1">
            {/* Render first 5 projects */}
            {visibleProjects.map((group) => (
                <Droppable key={group.id} id={group.id}>
                    {renderProject(group)}
                </Droppable>
            ))}

            {/* Render "See more" dropdown if there are more than 5 projects */}
            {hiddenProjects.length > 0 && (
                <div className="flex items-center justify-between w-full py-1 px-2 rounded-[8px] text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button className="flex items-center gap-2 w-full text-left group-data-[collapsible=icon]:justify-center">
                                    <div className="flex items-center justify-center w-6 h-6">
                                        <MoreHorizontal size={16} strokeWidth={strokeWidth} />
                                    </div>
                                    <span className="text-[14px] font-normal leading-normal group-data-[collapsible=icon]:hidden">
                                        {t('SEE_MORE')} ({hiddenProjects.length})
                                    </span>
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                side={isMobile ? "top" : "right"}
                                align={isMobile ? "end" : "start"}
                                className={`${isMobile ? "w-64 max-w-[calc(100vw-2rem)]" : "w-48"} max-h-80 overflow-y-auto`}
                                sideOffset={isMobile ? 8 : 4}
                                alignOffset={isMobile ? -8 : 0}
                            >
                                {hiddenProjects.map((group) => (
                                    <DropdownMenuItem
                                        key={group.id}
                                        onClick={() => handleProjectClickFromDropdown(group.id)}
                                        className="flex items-center gap-2"
                                    >
                                        <FolderClosed size={16} strokeWidth={strokeWidth} />
                                        <span className="truncate">{group.name}</span>
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            )}
        </div>
    );
};